import type { StatLine } from './types'

// Rate/derived stats don't scale with games/innings — they're already
// per-opportunity. Everything else on a StatLine is a counting stat we project
// to a full-season pace.
const RATE_KEYS = new Set<keyof StatLine>(['avg', 'obp', 'slg', 'ops', 'era', 'whip'])
const NON_STAT = new Set<keyof StatLine>(['label', 'team', 'teamId'])

// 200 IP — a full starter's season, carried in OUTS (200 × 3).
const TARGET_OUTS = 600

/**
 * Project each row's counting stats by `factorOf(row)`: counting stats are
 * multiplied and rounded, rate stats (AVG/OBP/.../ERA/WHIP) pass through, and
 * identity fields (label/team) are preserved so the row still renders its Year +
 * team link. A null factor (can't normalize — e.g. zero games/innings) passes
 * the row through untouched, never dividing by zero.
 *
 * IP is carried in OUTS here (formatted later by fmtIp), so scaling the outs and
 * rounding is correct — 480 outs ⇒ 160.0 IP, 600 outs ⇒ 200.0 IP.
 */
function project(rows: StatLine[], factorOf: (row: StatLine) => number | null): StatLine[] {
  return rows.map((row) => {
    const factor = factorOf(row)
    if (factor == null) return row
    const out: StatLine = { label: row.label, team: row.team, teamId: row.teamId }
    for (const key of Object.keys(row) as (keyof StatLine)[]) {
      if (NON_STAT.has(key)) continue
      const v = row[key]
      if (typeof v !== 'number') continue
      ;(out as Record<string, number>)[key] = RATE_KEYS.has(key) ? v : Math.round(v * factor)
    }
    return out
  })
}

/**
 * Position-player projection: counting stats to a full 160-game season
 * (`stat / G * 160`). G itself normalizes to 160.
 */
export function toPer160(rows: StatLine[]): StatLine[] {
  return project(rows, (row) => (row.g && row.g > 0 ? 160 / row.g : null))
}

/**
 * Pitcher projection: counting stats to a full 200-IP season (`stat / IP * 200`).
 * "Per 160 games" is meaningless for pitchers (a starter appears in ~32 games),
 * so we normalize on innings instead — IP itself becomes 200.0 (600 outs) and
 * G/GS/W/K scale to that workload.
 */
export function toPerInnings(rows: StatLine[]): StatLine[] {
  return project(rows, (row) => (row.ip && row.ip > 0 ? TARGET_OUTS / row.ip : null))
}
